import { useState } from 'react';
import { useSelector } from 'react-redux';

import { Form } from 'antd';

import getUserTimeZone from 'components/InterviewScheduler/utils/getUserTimeZone';

import { selectActiveUserId } from 'features/applications/applicationsSelectors';
import applicationsService from 'features/applications/applicationsService';

import useErrorCatch from 'hooks/useErrorCatch';
import { useInterviewSlots } from 'hooks/useInterviewSlots';

import { GTMEventLogger } from 'utils/analytics';

import { InterviewDetails } from 'types/interviewSlots';

import { GTMEvents } from 'constants/enums';

export const useInterviewConfirmation = (
  interviewRequest: InterviewDetails,
  onConfirmed: () => void,
  onLoading: (isLoading: boolean) => void
) => {
  const catchError = useErrorCatch();
  const [form] = Form.useForm();
  const meetingLink = Form.useWatch('meetingLink', form);
  const { selectedSlot } = useInterviewSlots();
  const applicationId = useSelector(selectActiveUserId);

  const [isJoblioZoomInterviewLink, setIsJoblioZoomInterviewLink] =
    useState<boolean>(false);

  const isValidUrl = (url: string) => {
    try {
      new URL(url);
      return (
        url.includes('zoom.us') ||
        url.includes('meet.google.com') ||
        url.includes('teams.microsoft.com')
      );
    } catch {
      return false;
    }
  };

  const canConfirmSlot =
    isJoblioZoomInterviewLink ||
    (meetingLink?.trim().length > 0 && isValidUrl(meetingLink.trim()));

  const handleChangeInterviewLink = (isCustom: boolean) => {
    setIsJoblioZoomInterviewLink(isCustom);
    if (isCustom) {
      form.setFieldsValue({ interviewLink: '' });

      GTMEventLogger(GTMEvents.ApplicationsListViewSidebarInterviewCustom);
    } else {
      GTMEventLogger(GTMEvents.ApplicationsListViewSidebarInterviewJoblio);
    }
  };

  const handleConfirmSlot = async () => {
    if (!selectedSlot || !interviewRequest) {
      return;
    }

    const payload = {
      setupMeeting: isJoblioZoomInterviewLink,
      slot: {
        timeZone: getUserTimeZone(),
        slotList: [selectedSlot]
      },
      ...(isJoblioZoomInterviewLink ? {} : { meetingLink: meetingLink?.trim() })
    };

    try {
      onLoading?.(true);
      await applicationsService.confirmInterview(
        applicationId,
        interviewRequest.id,
        payload
      );
      onConfirmed();

      const event = isJoblioZoomInterviewLink
        ? GTMEvents.ApplicationsListViewSidebarInterviewJoblioSent
        : GTMEvents.ApplicationsListViewSidebarInterviewCustomSent;

      GTMEventLogger(event);
      GTMEventLogger(
        GTMEvents.ApplicationsListViewSidebarInterviewSlotsSelected
      );
    } catch (error) {
      catchError({ error });
    } finally {
      onLoading?.(false);
    }
  };

  return {
    form,
    meetingLink,
    selectedSlot,
    isJoblioZoomInterviewLink,
    canConfirmSlot,
    isValidUrl,
    handleChangeInterviewLink,
    handleConfirmSlot
  };
};
